import * as Sentry from '@sentry/browser'
import { defineStore } from 'pinia'
import { Service } from '@/services/helpers/ServiceHelper'

type Post = {
  id: number
  title: string
  description: string
  date: string
  link: string
  icon: string
}

export const useNewFeaturesStore = defineStore('newFeatures', () => {
  const snackbar = useSnackbar()

  // STATE
  const posts = ref<Array<Post>>([])
  const latestTimestamp = ref<string | null>(null)
  // A string default keeps vueuse on its raw pass-through serializer, so the ISO
  // strings already in the field keep reading; a null default would switch it to JSON.
  const lastSeenTimestamp = useLocalStorage('lastSeenNewFeaturesTimestamp', '', { writeDefaults: false })

  // GETTERS
  const hasNewFeatures = computed(() => {
    if (!latestTimestamp.value) { return false }
    if (!lastSeenTimestamp.value) { return true }
    return new Date(latestTimestamp.value) > new Date(lastSeenTimestamp.value)
  })

  // ACTIONS
  const fetchFeatures = async () => {
    try {
      const { getEffectiveAuthToken } = useEffectiveAuthToken()
      if (!await getEffectiveAuthToken()) { return [] }
      const features = await Service.api.newFeaturesService?.getFeatures()
      if (!features) { return [] }
      posts.value = features.posts
      latestTimestamp.value = features.timestamp
      return posts.value
    } catch (error) {
      snackbar.error('Failed to load new posts.')
      Sentry.captureException(error)
      return []
    }
  }

  const markFeaturesAsSeen = () => {
    if (!latestTimestamp.value) { return }
    lastSeenTimestamp.value = latestTimestamp.value
  }

  return {
    // STATE
    posts,
    latestTimestamp,
    lastSeenTimestamp,

    // GETTERS
    hasNewFeatures,

    // ACTIONS
    fetchFeatures,
    markFeaturesAsSeen
  }
})
